

export default class ComboboxController {
    constructor(config = {}) {
        this.host = config.host;
        this.isMultiSelect = config.isMultiSelect === true;
        this.isSearchable = config.isSearchable === true;
        this.onSelect = typeof config.onSelect === 'function' ? config.onSelect : null;
        this.onFilter = typeof config.onFilter === 'function' ? config.onFilter : null;
        this.getOptionValue = config.getOptionValue || ((optionEl) => optionEl?.dataset?.value);
        this.getSelectedValue = config.getSelectedValue || (() => null);
        this.focusedIndex = -1;
        this._readOnlyStr = '';
        this._readOnlyStrTimeStamp = 0;
    }

    get triggerEl() {
        return this.host?.template?.querySelector('.slds-dropdown-trigger');
    }

    get inputEl() {
        return this.host?.template?.querySelector('input, .slds-combobox__input');
    }

    get optionEls() {
        return Array.from(
            this.host?.template?.querySelectorAll('.slds-listbox__option[role="option"]') || []
        );
    }

    get isOpen() {
        return this.triggerEl?.classList.contains('slds-is-open');
    }

    setInputExpanded(isExpanded) {
        const input = this.inputEl;
        if (input) {
            input.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        }
    }

    setInputFocusStyling(hasFocus) {
        const input = this.inputEl;
        if (input) {
            input.classList.toggle('slds-has-focus', hasFocus);
        }
    }

    clearFocusedOption() {
        this.optionEls.forEach((optionEl) => {
            optionEl.classList.remove('slds-has-focus');
            optionEl.setAttribute('aria-selected', 'false');
        });
        this.focusedIndex = -1;
    }

    clearSelectedOption() {
        this.optionEls.forEach((optionEl) => {
            optionEl.classList.remove('slds-is-selected');
            optionEl.setAttribute('aria-checked', 'false');
        });
    }

    focusOption(index) {
        const options = this.optionEls;
        if (!options.length) {
            this.focusedIndex = -1;
            return;
        }

        let normalizedIndex = index;

        if (normalizedIndex < 0) {
            normalizedIndex = options.length - 1;
        } else if (normalizedIndex >= options.length) {
            normalizedIndex = 0;
        }

        this.clearFocusedOption();

        const optionEl = options[normalizedIndex];
        optionEl.classList.add('slds-has-focus');
        optionEl.setAttribute('aria-selected', 'true');
        optionEl.scrollIntoView({ block: 'nearest' });

        this.focusedIndex = normalizedIndex;
    }

    focusFirstOrSelected() {
        const options = this.optionEls;
        if (!options.length) {
            this.focusedIndex = -1;
            return;
        }

        const selectedValue = this.getSelectedValue();
        const selectedIndex = options.findIndex((optionEl) => {
            return this.getOptionValue(optionEl) === selectedValue;
        });

        this.focusOption(selectedIndex >= 0 ? selectedIndex : 0);
    }

    open({ focusSelected = true } = {}) {
        if (!this.triggerEl) {
            return;
        }

        this.triggerEl.classList.add('slds-is-open');
        this.setInputExpanded(true);
        this.setInputFocusStyling(true);
        this.clearFocusedOption();

        if (focusSelected) {
            this.focusFirstOrSelected();
        }

        requestAnimationFrame(() => {
            this.handleDropdownFlip();
        })
    }

    close() {
        if (!this.triggerEl) {
            return;
        }

        this.triggerEl.classList.remove('slds-is-open');
        this.setInputExpanded(false);
        this.clearFocusedOption();
    }

    toggle() {
        if (this.isOpen) {
            this.close();
            return;
        }

        this.open({ focusSelected: true });
    }

    moveFocusNext() {
        const options = this.optionEls;
        if (!options.length) {
            return;
        }

        const nextIndex = this.focusedIndex < 0 ? 0 : this.focusedIndex + 1;
        this.focusOption(nextIndex);
    }

    moveFocusPrev() {
        const options = this.optionEls;
        if (!options.length) {
            return;
        }

        const prevIndex = this.focusedIndex < 0 ? options.length - 1 : this.focusedIndex - 1;
        this.focusOption(prevIndex);
    }

    selectFocusedOption() {
        const options = this.optionEls;
        if (!options.length || this.focusedIndex < 0) {
            return;
        }

        this.selectOptionElement(options[this.focusedIndex]);
    }

    selectOptionElement(optionEl) {
        if (!optionEl) {
            return;
        }

        if (!this.isMultiSelect) {
            this.clearSelectedOption();
        }

        optionEl.classList.add('slds-has-focus');
        optionEl.classList.add('slds-is-selected');
        optionEl.setAttribute('aria-selected', 'true');
        optionEl.setAttribute('aria-checked', 'true');

        if (this.onSelect) {
            this.onSelect({
                value: this.getOptionValue(optionEl),
                optionEl: optionEl
            });
        }

        if (!this.isMultiSelect) {
            this.close();
        }
    }

    handleTriggerClick() {
        this.toggle();
    }

    handleTriggerKeyDown(event) {
        const key = event.key;

        if (key === 'Enter' || key === ' ') {
            if (event.key === 'Enter') {
                event.preventDefault();
            }

            if (this.isOpen) {
                this.selectFocusedOption();
            } else {
                this.open({ focusSelected: true });
            }
            return;
        }

        if (key === 'ArrowDown') {
            event.preventDefault();

            if (!this.isOpen) {
                this.open({ focusSelected: false });
                this.focusOption(0);
                return;
            }

            this.moveFocusNext();
            return;
        }

        if (key === 'ArrowUp') {
            event.preventDefault();

            if (!this.isOpen) {
                this.open({ focusSelected: false });
                this.focusOption(0);
                return;
            }

            this.moveFocusPrev();
            return;
        }

        if (key === 'Escape' || key === 'Tab') {
            this.close();
        }
    }

    handleInputKeyDown(event) {
        const key = event.key;
        const ignoredKeys = [
            'Enter',
            'Escape',
            'Shift',
            'ArrowUp',
            'ArrowDown',
            'ArrowLeft',
            'ArrowRight',
            'Tab'
        ];
        if (ignoredKeys.includes(key)) {
            return;
        }

        if (this.isSearchable) {
            if (this.onFilter) {
                this.onFilter(event.target.value);
            }
            return;
        }

        if (!this.isSearchable) {
            this.open({ focusSelected: false });
            this.handleReadOnlyTypeAhead(event);
            return;
        }

        this.open({ focusSelected: false });
    }

    handleOptionClick(event) {
        const optionEl = event.currentTarget?.closest('.slds-listbox__option[role="option"]');
        this.selectOptionElement(optionEl);
        if (!this.isMultiSelect) {
            requestAnimationFrame(() => {
                this.close();
            })
        }
    }

    deflectFocus(event) {
        event.preventDefault();
    }

    handleFocus() {
        this.setInputFocusStyling(true);
    }

    handleBlur() {
        this.setInputFocusStyling(false);
        this.close();
    }

    handleDropdownFlip() {
        if (this.host?.suppressFlip) {return;}
        const dropdown = this.host?.template.querySelector('.slds-dropdown_fluid');
        if (!dropdown) return;
        if (this.host?.alwaysFlip) {
            if (!dropdown.classList.contains('dropdown-up')) {
                dropdown.classList.add('dropdown-up');
                return;
            }
        }
        // Reset any previous flip
        dropdown.classList.remove('dropdown-up');

        const rect = dropdown.getBoundingClientRect();
        const overflowBottom = rect.bottom > window.innerHeight;
        const overflowTop = rect.top < 0;

        // If it overflows below the viewport and has more space above, flip it
        if (overflowBottom && rect.top > rect.height) {
            dropdown.classList.add('dropdown-up');
        } else if (overflowTop) {
            // In case it somehow renders above the viewport already
            dropdown.classList.remove('dropdown-up');
        }
    }

    handleReadOnlySearch(event, currentStr, options) {
        const k = event?.key || '';
        const printable = k.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

        if (k === 'Backspace') {
            currentStr = currentStr.slice(0, -1);
        } else if (printable) {
            currentStr += k;
        } else {
            return { nextStr: currentStr, match: null, index: -1 };
        }

        const q = currentStr.trim().toLowerCase();
        if (!q) {
            return { nextStr: currentStr, match: null, index: -1 };
        }

        const normalize = (v) => {
            if (v == null) {
                return '';
            }
            if (v instanceof Date) {
                return v.toISOString();
            }
            return String(v);
        };

        for (let i = 0; i < options.length; i++) {
            if (normalize(options[i].label).toLowerCase().startsWith(q)) {
                return { nextStr: currentStr, match: options[i], index: i };
            }
        }

        for (let i = 0; i < options.length; i++) {
            if (normalize(options[i].label).toLowerCase().includes(q)) {
                return { nextStr: currentStr, match: options[i], index: i };
            }
        }

        for (let i = 0; i < options.length; i++) {
            if (normalize(options[i].value).toLowerCase().startsWith(q)) {
                return { nextStr: currentStr, match: options[i], index: i };
            }
        }

        for (let i = 0; i < options.length; i++) {
            if (normalize(options[i].value).toLowerCase().includes(q)) {
                return { nextStr: currentStr, match: options[i], index: i };
            }
        }

        return { nextStr: currentStr, match: null, index: -1 };
    }

    handleReadOnlyTypeAhead(event) {
        const diffMs = Date.now() - this._readOnlyStrTimeStamp;

        if (diffMs > 2000) {
            this._readOnlyStr = '';
        }

        const { nextStr, match, index } = this.handleReadOnlySearch(
            event,
            this._readOnlyStr,
            this.options || []
        );

        if (typeof nextStr === 'string') {
            this._readOnlyStr = nextStr;
            this._readOnlyStrTimeStamp = Date.now();

            if (match && index >= 0) {
                this.focusOption(index);
            }
        }
    }
    
}